import {
  PREDICTION_LEAD_MS,
  ROUND_ACTIVE_MS,
  ROUND_CYCLE_MS,
  type CompetitionPoolDetail,
  type CompetitionRoundResult,
  type PredictionDirection,
} from "@priceon/shared";

export type BattleRoundPhase =
  | "upcoming"
  | "pick-open"
  | "live"
  | "settling"
  | "won"
  | "lost"
  | "missed"
  | "void"
  | "closed";

export interface BattleRoundDisplay {
  key: string;
  roundId?: string;
  sequence: number;
  startsAt: number;
  endsAt: number;
  pickOpensAt: number;
  pickClosesAt: number;
  acceptingPick: boolean;
  status: CompetitionRoundResult["status"] | "pending";
  phase: BattleRoundPhase;
  result: PredictionDirection | null;
  myDirection: PredictionDirection | null;
  myOutcome: CompetitionRoundResult["myOutcome"];
  upPicks: number | null;
  downPicks: number | null;
}

type BattleTimelineSource = Pick<CompetitionPoolDetail, "myEntry" | "roundCount" | "rounds" | "startsAt">;

export function buildBattleTimeline(
  competition: BattleTimelineSource,
  now: number,
): BattleRoundDisplay[] {
  const poolStartsAt = Date.parse(competition.startsAt);
  const roundBySequence = new Map(
    competition.rounds.map((round) => [round.sequence, round]),
  );

  return Array.from({ length: competition.roundCount }, (_, index) => {
    const sequence = index + 1;
    const round = roundBySequence.get(sequence);
    const expectedStart = poolStartsAt + index * ROUND_CYCLE_MS;
    const startsAt = parseOrFallback(round?.startsAt, expectedStart);
    const endsAt = parseOrFallback(round?.endsAt, startsAt + ROUND_ACTIVE_MS);
    const expectedPickOpen = startsAt - PREDICTION_LEAD_MS;
    const pickOpensAt = parseOrFallback(round?.pickOpensAt, expectedPickOpen);
    const pickClosesAt = parseOrFallback(round?.pickClosesAt, startsAt);
    const acceptingPick = Boolean(
      round?.acceptingPick && round.roundId && now >= pickOpensAt && now < pickClosesAt,
    );

    return {
      key: `round-${sequence}`,
      roundId: round?.roundId,
      sequence,
      startsAt,
      endsAt,
      pickOpensAt,
      pickClosesAt,
      acceptingPick,
      status: round?.status ?? "pending",
      phase: getRoundPhase(round, Boolean(competition.myEntry), now, startsAt, endsAt, acceptingPick),
      result: round?.result ?? null,
      myDirection: round?.myDirection ?? null,
      myOutcome: round?.myOutcome ?? null,
      upPicks: round?.upPicks ?? null,
      downPicks: round?.downPicks ?? null,
    };
  });
}

export function getBattleProgressPercent(now: number, startsAt: string, endsAt: string): number {
  const start = Date.parse(startsAt);
  const end = Date.parse(endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100));
}

function getRoundPhase(
  round: CompetitionRoundResult | undefined,
  hasEntry: boolean,
  now: number,
  startsAt: number,
  endsAt: number,
  acceptingPick: boolean,
): BattleRoundPhase {
  if (round?.status === "cancelled" || round?.myOutcome === "void") return "void";
  if (round?.status === "closed") {
    if (round.result === null) return "void";
    if (!round.myDirection) return hasEntry ? "missed" : "closed";
    if (round.myOutcome === "won") return "won";
    if (round.myOutcome === "lost") return "lost";
    return "settling";
  }
  if (now >= endsAt) return "settling";
  if (now >= startsAt) return "live";
  if (acceptingPick) return "pick-open";
  return "upcoming";
}

function parseOrFallback(value: string | undefined, fallback: number): number {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}
